import type { ColorName } from '@emotion/react';
import {
  Card,
  type CardProps,
  type IconName,
  Visible,
} from 'design-system/base';
import { useIsSmallerThan } from 'hooks/useResponsive';
import { useToggle } from 'hooks/useToggle';
import type React from 'react';
import type { PropsWithChildren } from 'react';

import { Header } from '../header';

interface BaseToggleCardProps {
  title: string;
  textExpand?: string;
  textCollapse?: string;
  initialExpanded?: boolean;
  blockColor?: ColorName;
  iconColor?: ColorName;
  icon?: IconName;
  iconText?: string;
  /**
   * Any overrides to be added to the internal card. Currently configured as default in order to
   * have a good default content layout
   */
  contentWrapperProps?: CardProps;
  alwaysExpanded?: boolean;
  /**
   * Secondary button props
   */
  secondaryButtonText?: string;
  showSecondaryButton?: boolean;
  setSecondaryButtonPressed?: () => void;
  isSecondaryButtonPressed?: boolean;
}

export type ToggleCardProps = PropsWithChildren<BaseToggleCardProps>;

export const ToggleCard: React.FC<ToggleCardProps> = ({
  textCollapse,
  textExpand,
  title,
  initialExpanded = false,
  blockColor,
  iconColor = 'text-on-color',
  icon,
  iconText,
  children,
  contentWrapperProps = { unsetZIndex: true },
  alwaysExpanded = false,
  secondaryButtonText,
  showSecondaryButton = false,
  setSecondaryButtonPressed,
  isSecondaryButtonPressed,
}) => {
  const [expanded, toggle] = useToggle(initialExpanded || alwaysExpanded);
  const isSmall = useIsSmallerThan('lg');

  const buttonText = expanded ? textCollapse : textExpand;
  const buttonIcon: IconName = expanded ? 'arrow-drop-up' : 'arrow-drop-down';
  const hideButtonText = !isSmall ? buttonText : undefined;

  const showSecondaryButtonText =
    !expanded && showSecondaryButton ? secondaryButtonText : undefined;
  const showSecondaryButtonIcon =
    !expanded && showSecondaryButton ? buttonIcon : undefined;

  const hideSecondaryButtonText = !isSmall
    ? showSecondaryButtonText
    : undefined;

  const onPrimaryButtonPress = () => {
    if (setSecondaryButtonPressed && isSecondaryButtonPressed) {
      setSecondaryButtonPressed();
    }
    toggle();
  };
  const onSecondaryButtonPress = () => {
    if (setSecondaryButtonPressed) {
      setSecondaryButtonPressed();
    }
    toggle();
  };

  return (
    <Card paddingHorizontal={0} paddingVertical={0} unsetZIndex>
      <Header
        title={title}
        primaryButtonText={hideButtonText}
        primaryButtonIcon={buttonIcon}
        blockColor={blockColor}
        iconColor={iconColor}
        icon={icon}
        iconText={iconText}
        onPrimaryButtonPress={onPrimaryButtonPress}
        showPrimaryButton={!alwaysExpanded}
        onSecondaryButtonPress={onSecondaryButtonPress}
        showSecondaryButton={!alwaysExpanded && showSecondaryButton}
        secondaryButtonIcon={showSecondaryButtonIcon}
        secondaryButtonText={hideSecondaryButtonText}
      />
      <Visible if={expanded}>
        <Card {...contentWrapperProps} unsetZIndex>
          {children}
        </Card>
      </Visible>
    </Card>
  );
};
